import { FloatingFormItem } from '@/components/_common';

import { useNumberInutilizationList } from '@/core/domain/invoice/numberInutilization/numberInutilization.hooks';
import {
  useSpedDocumentInvoices,
  useSpedDocumentManageImports,
} from '@/core/domain/sped/sped-documents/sped-documents.hooks';
import { SpedDocumentStatus } from '@/core/domain/sped/sped-documents/sped-documents.types';
import { useDebounce, useDisclosure } from '@/hooks';
import { useSpedDocumentContext } from '@/pages/FiscalPages/SpedPage/SpedDocumentsPage/SpedDocumentDetailPage';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Button, Col, Form, Input, Modal, Row, Table, Typography } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

interface FormValues {
  startNumber?: number;
  endNumber?: number;
}

export default function SpedDocumentDetailImportInutilization() {
  const spedDocument = useSpedDocumentContext();

  const hasImportPermission =
    hasPermissions([Authorities.SPED_DOCUMENT_IMPORT_PURCHASE]) &&
    !!spedDocument?.status &&
    [
      SpedDocumentStatus.AWAITING_SELECTION,
      SpedDocumentStatus.REOPENED,
      SpedDocumentStatus.REJECTED,
    ].includes(spedDocument?.status);

  const [form] = Form.useForm<FormValues>();

  const formValues = Form.useWatch([], form);

  const periodLabel = moment([
    Number(spedDocument?.assessmentYear),
    Number(spedDocument?.assessmentMonth) - 1,
    1,
  ]).format('MMMM/YYYY');

  const [opened, action] = useDisclosure();

  const history = useHistory();

  const [idsSelected, setIdsSelected] = useState<number[]>([]);

  const spedDocumentManageImports = useSpedDocumentManageImports();

  const filter = useDebounce(formValues);

  const inutilizationList = useNumberInutilizationList(
    {
      ...filter,
      companyId: [spedDocument?.companyId as number],
      inutilizationReferenceMonth: `${spedDocument?.assessmentYear}-${spedDocument?.assessmentMonth}-01`,
    },
    opened,
  );

  const inutilizations = flatInfiniteData(inutilizationList.data);

  const { data: invoicesImported } = useSpedDocumentInvoices({
    spedDocumentId: spedDocument?.id as number,
  });

  useEffect(() => {
    if (inutilizations?.length) {
      setIdsSelected(
        inutilizations
          .filter(
            (invoice) =>
              !invoicesImported?.some(
                (imported) => imported.invoiceId === invoice.id,
              ),
          )
          .map((invoice) => invoice.id),
      );
    }
  }, [invoicesImported]);

  const handleClose = () => {
    setIdsSelected([]);
    form.resetFields();
    action.close();
  };

  const handleSubmit = async () => {
    if (!spedDocument) return;

    await spedDocumentManageImports.mutateAsync({
      importType: 'INUTILIZATION',
      spedDocumentId: spedDocument?.id,
      items: idsSelected.map((id) => ({
        entityId: id,
        entityEnum: 'INUTILIZATION',
      })),
    });

    handleClose();
    history.push('?tab=inutilization');
  };

  return (
    <>
      <Button
        onClick={action.open}
        block
        type="primary"
        disabled={!hasImportPermission}
      >
        Importar Inutilização
      </Button>

      <Modal
        width={900}
        open={opened}
        onCancel={handleClose}
        title="Importação de faixas de inutilizações"
        okText="Importar"
        okButtonProps={{ disabled: idsSelected.length < 1 }}
        onOk={handleSubmit}
        confirmLoading={spedDocumentManageImports.isLoading}
      >
        <Typography.Title level={4}>
          Faixas de numerações inutilizadas emitidas do período ({periodLabel})
        </Typography.Title>

        <Form form={form}>
          <Row style={{ marginBottom: 24, marginTop: 24 }} gutter={[16, 16]}>
            <Col span={24} sm={4} md={6}>
              <FloatingFormItem name="startNumber" label="Númeração inicial">
                <Input />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={4} md={6}>
              <FloatingFormItem name="endNumber" label="Númeração final">
                <Input />
              </FloatingFormItem>
            </Col>
          </Row>
        </Form>

        <Table
          loading={inutilizationList.isLoading}
          dataSource={inutilizations}
          pagination={false}
          rowKey={(item) => item.id}
          rowSelection={{
            selectedRowKeys: idsSelected,
            onChange: (keys) => setIdsSelected(keys as number[]),
            getCheckboxProps: (item) => ({
              disabled:
                spedDocumentManageImports.isLoading ||
                !!invoicesImported?.some(
                  (imported) => imported.invoiceId === item.id,
                ),
            }),
          }}
          scroll={{ x: true }}
          columns={[
            { dataIndex: 'companyName', title: 'Empresa' },
            { dataIndex: 'startNumber', title: 'Numeração inicial' },
            { dataIndex: 'endNumber', title: 'Numeração final' },
            { dataIndex: 'model', title: 'Modelo' },
            { dataIndex: 'serie', title: 'Série' },
            {
              dataIndex: 'inutilizationDate',
              title: 'Dt. da inutilização',
              render: (_, item) =>
                item.inutilizationDate
                  ? moment(item.inutilizationDate).format('DD/MM/YYYY')
                  : '-',
            },
          ]}
        />
      </Modal>
    </>
  );
}
